import React from 'react'
import Money from 'uiComponents/money'
import { IOrder, IOrderItem, OrderTicketStatus } from 'orders/schema'
import RedeemedOn from 'orders/orders/ordersTable/orderDetails/redeemedOn'
import { Link } from 'react-router-dom'
import OrderItemStatus from 'orders/orders/ordersTable/orderDetails/orderItemStatus'
import ItemSection from './components/itemSection'
import SectionDataGrid from './components/sectionDataGrid'
import './styles.scss'
import ValidTo from 'orders/orders/commons/validTo'
import { convertTimeFormat, DateFormats, formatISOString } from 'utils/dates'
import { useAppSelector } from 'store/hooks'
import { getSelectedOrder } from 'orders/orders/redux'
import { CopyIcon } from 'uiComponents/buttons/copyIcon'
import { useTranslation } from 'translations'

interface ItemDetailsProps {
    item: IOrderItem
    order?: IOrder
    accountSlug: string
    isItemSelected: boolean
    isItemCanceledRefunded: boolean
    shouldShowPricesWithoutTax: boolean
    shouldShowBookingFees: boolean
}

const ItemDetailsSection = ({
    item,
    order,
    accountSlug,
    isItemSelected,
    isItemCanceledRefunded,
    shouldShowPricesWithoutTax,
    shouldShowBookingFees,
}: ItemDetailsProps) => {
    const { t } = useTranslation()
    const selectedOrder = order ?? useAppSelector(getSelectedOrder)
    const detailsData = React.useMemo(
        () => [
            {
                name: t('orders.orders.orderDetails.itemDetails.barcode'),
                value: (
                    <>
                        {item.barcode?.barcode}
                        <CopyIcon text={item.barcode?.barcode} />
                    </>
                ),
            },
            {
                name: t('orders.orders.orderDetails.itemDetails.productId'),
                value: (
                    <Link
                        title={item.productNumericId.toString()}
                        to={`/account/${accountSlug}/products/crud/${item.productId}`}
                        target="_blank"
                    >
                        {item.productNumericId}
                    </Link>
                ),
            },
            { name: t('orders.orders.orderDetails.itemDetails.redeemedOn'), value: <RedeemedOn value={item.barcode?.redeemed ?? undefined} /> },
            {
                name: t('orders.orders.orderDetails.itemDetails.visitDate'),
                value: item.visitDate ? formatISOString(item.visitDate, DateFormats.LONG_DATE) : '-',
            },
            {
                name: t('orders.orders.orderDetails.itemDetails.feesExclTax'),
                value: <Money amount={item.bookingFeeExclTax} accountSlug={accountSlug} />,
                hide: !shouldShowBookingFees || !shouldShowPricesWithoutTax,
            },
            {
                name: t('orders.orders.orderDetails.itemDetails.fees'),
                value: <Money amount={item.bookingFeeInclTax} accountSlug={accountSlug} />,
                hide: !shouldShowBookingFees,
            },
            {
                name: t('orders.orders.orderDetails.itemDetails.validFrom'),
                value: item.validFrom ? formatISOString(item.validFrom, DateFormats.LONG_DATE) : '-',
            },
            {
                name: t('orders.orders.orderDetails.itemDetails.validTo'),
                value: item.validTo ? (
                    <ValidTo status={item.barcode?.status ?? ''} orderItem={item} order={selectedOrder} />
                ) : (
                    '-'
                ),
            },
            {
                name: t('orders.orders.orderDetails.itemDetails.visitTime'),
                value: item.visitTime ? convertTimeFormat(item.visitTime, 'HH:mm:ss', DateFormats.SHORT_TIME) : '-',
            },
            {
                name: t('orders.orders.orderDetails.itemDetails.status'),
                value: <OrderItemStatus value={item.status as OrderTicketStatus} />,
            },
        ],
        [item, accountSlug, t],
    )

    return (
        <ItemSection
            sectionName={t('orders.orders.orderDetails.itemDetails.title')}
            isItemSelected={isItemSelected}
            isItemCanceledRefunded={isItemCanceledRefunded}
        >
            <SectionDataGrid dataItems={detailsData} />
        </ItemSection>
    )
}

export default ItemDetailsSection
